import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

export interface AvailabilitySummary {
  week: string;
  month: string;
  quarter: string;
  semester: string;
}

export type TimeUnit = "week" | "month" | "quarter" | "semester";

/** Returns the ISO date (YYYY-MM-DD) of the last day of the last complete period,
 *  or null if no complete period exists before the cutoff or before minDateISO. */
export function resolveLatestAvailableEndDate(
  cutoffISO: string,
  timeUnit: TimeUnit,
  minDateISO?: string | null,
): string | null {
  const cutoff = dayjs(`${cutoffISO}T00:00:00`);
  const minDate = minDateISO ? dayjs(`${minDateISO}T00:00:00`) : null;
  switch (timeUnit) {
    case "week": {
      const lastSunday = cutoff.subtract(cutoff.day(), "day");
      if (lastSunday.isSame(cutoff, "day") || lastSunday.isBefore(cutoff)) {
        // Only complete if the full week (Mon–Sun) ended before or on cutoff
        const periodEnd = cutoff.subtract(cutoff.day(), "day");
        // If today is Sunday (day 0), that Sunday is the end of the complete week
        if (cutoff.day() === 0) {
          if (minDate && cutoff.isBefore(minDate)) return null;
          return cutoff.format("YYYY-MM-DD");
        }
        // Otherwise go to last Sunday
        if (minDate && periodEnd.isBefore(minDate)) return null;
        return periodEnd.format("YYYY-MM-DD");
      }
      return null;
    }
    case "month": {
      const lastDayThisMonth = cutoff.endOf("month").startOf("day");
      if (!lastDayThisMonth.isAfter(cutoff)) {
        if (minDate && lastDayThisMonth.isBefore(minDate)) return null;
        return lastDayThisMonth.format("YYYY-MM-DD");
      }
      // Month not yet complete — use last day of previous month
      const prevMonthEnd = cutoff.subtract(1, "month").endOf("month").startOf("day");
      if (minDate && prevMonthEnd.isBefore(minDate)) return null;
      return prevMonthEnd.format("YYYY-MM-DD");
    }
    case "quarter": {
      const y = cutoff.year();
      const candidates = [
        { end: dayjs(`${y - 1}-12-31`) },
        { end: dayjs(`${y}-03-31`) },
        { end: dayjs(`${y}-06-30`) },
        { end: dayjs(`${y}-09-30`) },
        { end: dayjs(`${y}-12-31`) },
      ];
      const last = candidates.filter((q) => !q.end.isAfter(cutoff)).at(-1);
      if (!last) return null;
      if (minDate && last.end.isBefore(minDate)) return null;
      return last.end.format("YYYY-MM-DD");
    }
    case "semester": {
      const y = cutoff.year();
      const candidates = [
        { end: dayjs(`${y - 1}-12-31`) },
        { end: dayjs(`${y}-06-30`) },
        { end: dayjs(`${y}-12-31`) },
      ];
      const last = candidates.filter((s) => !s.end.isAfter(cutoff)).at(-1);
      if (!last) return null;
      if (minDate && last.end.isBefore(minDate)) return null;
      return last.end.format("YYYY-MM-DD");
    }
  }
}

/** Returns a human-readable summary string for the resolved endpoint.
 *  Format: "Resolved [time unit] endpoint: [last complete period] based on dataset availability through [date]."
 *  Returns null if no complete period exists or if the period is before minDateISO. */
export function resolveLatestAvailableSummary(
  cutoffISO: string,
  timeUnit: TimeUnit,
  minDateISO?: string | null,
): string | null {
  const cutoff = dayjs(`${cutoffISO}T00:00:00`);
  const minDate = minDateISO ? dayjs(`${minDateISO}T00:00:00`) : null;
  const formattedCutoff = cutoff.format("MMM D, YYYY");

  const unitLabel =
    timeUnit === "week"
      ? "weekly"
      : timeUnit === "month"
        ? "monthly"
        : timeUnit === "quarter"
          ? "quarterly"
          : "semester";

  switch (timeUnit) {
    case "week": {
      const lastSunday = cutoff.subtract(cutoff.day(), "day");
      if (lastSunday.isSame(cutoff, "day") && cutoff.day() !== 0) {
        return null;
      }
      const sunday = cutoff.day() === 0 ? cutoff : cutoff.subtract(cutoff.day(), "day");
      if (minDate && sunday.isBefore(minDate)) return null;
      const monday = sunday.subtract(6, "day");
      const weekNum = sunday.isoWeek();
      const year = sunday.isoWeekYear();
      const periodLabel = `Week ${weekNum}, ${year} (${monday.format("MMM D")}–${sunday.format("MMM D, YYYY")})`;
      return `Resolved ${unitLabel} endpoint: ${periodLabel} based on dataset availability through ${formattedCutoff}.`;
    }
    case "month": {
      const lastDayThisMonth = cutoff.endOf("month").startOf("day");
      const resolvedMonth = !lastDayThisMonth.isAfter(cutoff)
        ? cutoff
        : cutoff.subtract(1, "month");
      const periodEnd = resolvedMonth.endOf("month").startOf("day");
      if (minDate && periodEnd.isBefore(minDate)) return null;
      const periodLabel = resolvedMonth.format("MMMM YYYY");
      return `Resolved ${unitLabel} endpoint: ${periodLabel} based on dataset availability through ${formattedCutoff}.`;
    }
    case "quarter": {
      const y = cutoff.year();
      const candidates = [
        { end: dayjs(`${y - 1}-12-31`), label: `Q4 ${y - 1}` },
        { end: dayjs(`${y}-03-31`), label: `Q1 ${y}` },
        { end: dayjs(`${y}-06-30`), label: `Q2 ${y}` },
        { end: dayjs(`${y}-09-30`), label: `Q3 ${y}` },
        { end: dayjs(`${y}-12-31`), label: `Q4 ${y}` },
      ];
      const last = candidates.filter((q) => !q.end.isAfter(cutoff)).at(-1);
      if (!last) return null;
      if (minDate && last.end.isBefore(minDate)) return null;
      return `Resolved ${unitLabel} endpoint: ${last.label} based on dataset availability through ${formattedCutoff}.`;
    }
    case "semester": {
      const y = cutoff.year();
      const candidates = [
        { end: dayjs(`${y - 1}-12-31`), label: `S2 ${y - 1}` },
        { end: dayjs(`${y}-06-30`), label: `S1 ${y}` },
        { end: dayjs(`${y}-12-31`), label: `S2 ${y}` },
      ];
      const last = candidates.filter((s) => !s.end.isAfter(cutoff)).at(-1);
      if (!last) return null;
      if (minDate && last.end.isBefore(minDate)) return null;
      return `Resolved ${unitLabel} endpoint: ${last.label} based on dataset availability through ${formattedCutoff}.`;
    }
  }
}

export function computeAvailabilitySummary(cutoffISO: string, minDateISO?: string): AvailabilitySummary {
  const cutoff = dayjs(`${cutoffISO}T00:00:00`);
  const minDate = minDateISO ? dayjs(`${minDateISO}T00:00:00`) : null;
  return {
    week: getLastCompleteWeek(cutoff, minDate),
    month: getLastCompleteMonth(cutoff, minDate),
    quarter: getLastCompleteQuarter(cutoff, minDate),
    semester: getLastCompleteSemester(cutoff, minDate),
  };
}

// ISO week: Mon–Sun. Complete when the Sunday of that week ≤ cutoff.
function getLastCompleteWeek(cutoff: Dayjs, minDate: Dayjs | null): string {
  // day(): 0=Sun, 1=Mon, ..., 6=Sat
  const lastSunday = cutoff.subtract(cutoff.day(), "day");
  const monday = lastSunday.subtract(6, "day");
  // Period must end on or after minDate (dataset must have data in this period)
  if (minDate && lastSunday.isBefore(minDate)) return "None available";
  const weekNum = lastSunday.isoWeek();
  const year = lastSunday.isoWeekYear();
  return `Week ${weekNum}, ${year} (${monday.format("MMM D")}–${lastSunday.format("MMM D")})`;
}

// Complete when the last day of the month ≤ cutoff.
function getLastCompleteMonth(cutoff: Dayjs, minDate: Dayjs | null): string {
  const lastDayOfMonth = cutoff.endOf("month").startOf("day");
  const resolvedMonth = !lastDayOfMonth.isAfter(cutoff)
    ? cutoff
    : cutoff.subtract(1, "month");
  const periodEnd = resolvedMonth.endOf("month").startOf("day");
  if (minDate && periodEnd.isBefore(minDate)) return "None available";
  return resolvedMonth.format("MMMM YYYY");
}

// Complete when Mar 31 / Jun 30 / Sep 30 / Dec 31 ≤ cutoff.
function getLastCompleteQuarter(cutoff: Dayjs, minDate: Dayjs | null): string {
  const y = cutoff.year();
  const candidates = [
    { end: dayjs(`${y - 1}-12-31`), label: `Q4 ${y - 1}` },
    { end: dayjs(`${y}-03-31`), label: `Q1 ${y}` },
    { end: dayjs(`${y}-06-30`), label: `Q2 ${y}` },
    { end: dayjs(`${y}-09-30`), label: `Q3 ${y}` },
    { end: dayjs(`${y}-12-31`), label: `Q4 ${y}` },
  ];
  const last = candidates.filter((q) => !q.end.isAfter(cutoff)).at(-1);
  if (!last) return "None available";
  if (minDate && last.end.isBefore(minDate)) return "None available";
  return last.label;
}

// Complete when Jun 30 / Dec 31 ≤ cutoff.
function getLastCompleteSemester(cutoff: Dayjs, minDate: Dayjs | null): string {
  const y = cutoff.year();
  const candidates = [
    { end: dayjs(`${y - 1}-12-31`), label: `S2 ${y - 1}` },
    { end: dayjs(`${y}-06-30`), label: `S1 ${y}` },
    { end: dayjs(`${y}-12-31`), label: `S2 ${y}` },
  ];
  const last = candidates.filter((s) => !s.end.isAfter(cutoff)).at(-1);
  if (!last) return "None available";
  if (minDate && last.end.isBefore(minDate)) return "None available";
  return last.label;
}
